import { rpx } from 'client/lib/rpx-client';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { LoadingIndicator } from '@components/loading-indicator';
import { showError } from '@components/app-error';
import { classifyAccount } from 'shared/account-classification';
import dayjs from 'dayjs';

type Lesson = Awaited<
  ReturnType<typeof rpx.coursesOverview.getCourseLessonActivity>
>['lessons'][number];

type Visitor = Awaited<
  ReturnType<typeof rpx.coursesOverview.getLessonVisitors>
>['visitors'][number];

export function CourseLessonActivityDrawer(props: {
  courseId: UUID;
  title: string;
  onClose: () => void;
}) {
  const [lessons, setLessons] = useState<Lesson[] | undefined>(undefined);
  const [query, setQuery] = useState('');
  const [hideZero, setHideZero] = useState(false);
  const [expandedLessonId, setExpandedLessonId] = useState<UUID | undefined>(undefined);
  const [visitorsByLesson, setVisitorsByLesson] = useState<Record<string, Visitor[]>>({});
  const [loadingVisitors, setLoadingVisitors] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let cancelled = false;
    setLessons(undefined);
    rpx.coursesOverview
      .getCourseLessonActivity({ courseId: props.courseId })
      .then((result) => {
        if (cancelled) {
          return;
        }
        setLessons(result.lessons);
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }
        showError(err);
      });
    return () => {
      cancelled = true;
    };
  }, [props.courseId]);

  function toggleExpanded(lessonId: UUID) {
    if (expandedLessonId === lessonId) {
      setExpandedLessonId(undefined);
      return;
    }
    setExpandedLessonId(lessonId);
    if (visitorsByLesson[lessonId] || loadingVisitors[lessonId]) {
      return;
    }
    setLoadingVisitors((prev) => ({ ...prev, [lessonId]: true }));
    rpx.coursesOverview
      .getLessonVisitors({ lessonId })
      .then((result) => {
        setVisitorsByLesson((prev) => ({ ...prev, [lessonId]: result.visitors }));
      })
      .catch((err) => showError(err))
      .finally(() => {
        setLoadingVisitors((prev) => ({ ...prev, [lessonId]: false }));
      });
  }

  const filtered = useMemo(() => {
    if (!lessons) {
      return [];
    }
    let rows = lessons;
    const q = query.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (l) => l.title.toLowerCase().includes(q) || (l.moduleTitle || '').toLowerCase().includes(q),
      );
    }
    if (hideZero) {
      rows = rows.filter((l) => l.uniqueVisitors > 0);
    }
    return rows;
  }, [lessons, query, hideZero]);

  return createPortal(
    <div class="fixed inset-0 z-[1000] bg-black/30" onClick={props.onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${props.title} — Lesson activity`}
        onClick={(e) => e.stopPropagation()}
        class="absolute right-0 top-0 flex h-full w-full max-w-3xl flex-col border-l border-gray-200 bg-white shadow-xl"
      >
        <header class="flex items-start justify-between border-b border-gray-200 px-5 py-4">
          <div>
            <h2 class="text-base font-semibold text-gray-900">
              {props.title} <span class="text-sm font-normal text-gray-500">— Lesson activity</span>
            </h2>
            <p class="text-xs text-gray-500">
              Lessons in this course with view counts since page tracking turned on. Click a row to
              see who visited.
            </p>
          </div>
          <button
            type="button"
            class="text-sm text-gray-500 hover:text-gray-700"
            onClick={props.onClose}
          >
            Close
          </button>
        </header>
        <div class="border-b border-gray-200 px-5 py-3">
          <input
            type="text"
            value={query}
            onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
            placeholder="Search by lesson or module title…"
            class="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
          <label class="mt-2 flex items-center gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={hideZero}
              onChange={(e) => setHideZero((e.target as HTMLInputElement).checked)}
            />
            Hide lessons with no visitors
          </label>
        </div>
        <div class="flex-1 overflow-y-auto">
          {!lessons ? (
            <div class="p-6">
              <LoadingIndicator />
            </div>
          ) : filtered.length === 0 ? (
            <p class="p-6 text-sm text-gray-500">
              {lessons.length === 0
                ? 'No lessons in this course.'
                : 'No lessons match these filters.'}
            </p>
          ) : (
            <ul class="divide-y divide-gray-100">
              {filtered.map((l) => (
                <LessonRow
                  key={l.lessonId}
                  lesson={l}
                  expanded={expandedLessonId === l.lessonId}
                  onToggle={() => toggleExpanded(l.lessonId)}
                  visitors={visitorsByLesson[l.lessonId]}
                  isLoading={loadingVisitors[l.lessonId]}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function LessonRow(props: {
  lesson: Lesson;
  expanded: boolean;
  onToggle: () => void;
  visitors: Visitor[] | undefined;
  isLoading: boolean | undefined;
}) {
  const { lesson, expanded, onToggle, visitors, isLoading } = props;
  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        class="flex w-full items-baseline gap-3 px-5 py-3 text-left hover:bg-gray-50"
      >
        <span class="text-xs text-gray-400" aria-hidden="true">
          {expanded ? '▾' : '▸'}
        </span>
        <div class="min-w-0 flex-1">
          <p class="truncate text-sm font-medium text-gray-900">{lesson.title}</p>
          <p class="truncate text-xs text-gray-500">{lesson.moduleTitle}</p>
        </div>
        <div class="shrink-0 text-right">
          <div class="text-sm tabular-nums text-gray-900">
            {lesson.uniqueVisitors}
            <span class="ml-1 text-xs font-normal text-gray-500">
              {lesson.uniqueVisitors === 1 ? 'visitor' : 'visitors'}
            </span>
          </div>
          <div class="text-xs tabular-nums text-gray-500">{lesson.totalVisits} total views</div>
        </div>
      </button>
      {expanded && (
        <div class="border-t border-gray-100 bg-gray-50 px-5 py-3">
          {isLoading || !visitors ? (
            <LoadingIndicator />
          ) : visitors.length === 0 ? (
            <p class="text-sm text-gray-500">No one has visited this lesson yet.</p>
          ) : (
            <VisitorList visitors={visitors} />
          )}
        </div>
      )}
    </li>
  );
}

function VisitorList(props: { visitors: Visitor[] }) {
  return (
    <ul class="flex flex-col gap-1.5 text-sm">
      {props.visitors.map((v) => {
        const kind = classifyAccount(v.email, v.name);
        return (
          <li
            key={v.userId}
            class="flex items-baseline justify-between gap-3 rounded-md bg-white px-3 py-2"
          >
            <div class="min-w-0 flex-1">
              <div class="flex items-baseline gap-2">
                <span class="truncate font-medium text-gray-900">{v.name || v.email}</span>
                {kind !== 'member' && <KindPill kind={kind} />}
              </div>
              <div class="truncate text-xs text-gray-500">{v.email}</div>
            </div>
            <div class="shrink-0 text-right">
              <div class="text-sm tabular-nums text-gray-700">
                {v.visitCount}
                <span class="ml-1 text-xs font-normal text-gray-500">
                  {v.visitCount === 1 ? 'view' : 'views'}
                </span>
              </div>
              <div class="text-xs tabular-nums text-gray-500">
                last {dayjs(v.lastVisitedAt).format('MMM D')}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function KindPill(props: { kind: 'staff' | 'test' }) {
  const cls = props.kind === 'staff' ? 'bg-indigo-50 text-indigo-700' : 'bg-gray-100 text-gray-500';
  const label = props.kind === 'staff' ? 'Staff' : 'Test';
  return <span class={`rounded px-1.5 py-0.5 text-[10px] font-medium ${cls}`}>{label}</span>;
}
